import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import api from '@/admin/lib/api'
import type {
  PulseWebhookAuthInput,
  PulseWebhookSubscription,
} from '@/admin/types/pulse'

const QUERY_KEY = ['pulse-webhooks'] as const

interface ListResult<T> {
  count: number
  results: T[]
}

interface PaginatedResult<T> {
  count: number
  page: number
  page_size: number
  next: string | null
  previous: string | null
  results: T[]
}

// Tenant-admin (in-app) webhooks endpoint. Same wire shape as the
// partner-facing /api/v1/webhooks/ - both surfaces share
// `project_webhook` server-side - but this one uses session auth and
// only requires any pulse_* feature flag, not `public_api`.
export function usePulseWebhooks(params?: Record<string, string>) {
  const queryClient = useQueryClient()

  const query = useQuery<ListResult<PulseWebhookSubscription>>({
    queryKey: [...QUERY_KEY, params],
    queryFn: () =>
      api
        .get('/admin/webhooks/', { params })
        .then((r) => r.data as ListResult<PulseWebhookSubscription>),
    staleTime: 30_000,
  })

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  }, [queryClient])

  return { ...query, invalidate }
}

// Single webhook detail. Same wire shape as the list rows.
export function usePulseWebhook(id: string | undefined) {
  const queryClient = useQueryClient()

  const query = useQuery<PulseWebhookSubscription>({
    queryKey: ['pulse-webhook', id],
    queryFn: () =>
      api
        .get(`/admin/webhooks/${id}/`)
        .then((r) => r.data as PulseWebhookSubscription),
    staleTime: 30_000,
    enabled: Boolean(id),
  })

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pulse-webhook', id] })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  }, [queryClient, id])

  return { ...query, invalidate }
}

// ── Webhook actions ────────────────────────────────────────────────
//
// One hook returning every mutation the detail page needs. Each
// mutation invalidates the relevant queries on success so the UI
// re-renders without manual refresh.

export interface UpdateWebhookInput {
  description?: string
  target_url?: string
  max_attempts?: number
  enabled_events?: string[]
  is_active?: boolean
  /** Replaces the stored endpoint-auth config wholesale; omit to keep it. */
  auth?: PulseWebhookAuthInput
}

interface FireTestResult {
  delivery_id: string
}

interface RotateSecretResult {
  secret: string
  // Old secret stays valid for this window so in-flight deliveries
  // don't fail HMAC verification on receivers that haven't deployed
  // the new secret yet. Omitted once the window has closed.
  previous_secret_expires_at?: string
}

interface DeleteResult {
  hard_deleted: boolean
  message: string
}

export function useWebhookActions(id: string | undefined) {
  const queryClient = useQueryClient()

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pulse-webhook', id] })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    queryClient.invalidateQueries({
      queryKey: ['pulse-webhook-deliveries', id],
    })
  }, [queryClient, id])

  const fireTest = useMutation<FireTestResult>({
    mutationFn: () =>
      api.post(`/admin/webhooks/${id}/test/`).then((r) => r.data),
    onSuccess: invalidateAll,
  })

  const rotateSecret = useMutation<RotateSecretResult>({
    mutationFn: () =>
      api.post(`/admin/webhooks/${id}/rotate_secret/`).then((r) => r.data),
  })

  const update = useMutation<PulseWebhookSubscription, unknown, UpdateWebhookInput>({
    mutationFn: (body) =>
      api.put(`/admin/webhooks/${id}/`, body).then((r) => r.data),
    onSuccess: invalidateAll,
  })

  const remove = useMutation<DeleteResult>({
    mutationFn: () => api.delete(`/admin/webhooks/${id}/`).then((r) => r.data),
    onSuccess: invalidateAll,
  })

  const reactivate = useMutation<PulseWebhookSubscription>({
    mutationFn: () =>
      api.post(`/admin/webhooks/${id}/reactivate/`).then((r) => r.data),
    onSuccess: invalidateAll,
  })

  return { fireTest, rotateSecret, update, remove, reactivate }
}

// ── Deliveries for a single webhook ────────────────────────────────

export interface PulseDeliveryRow {
  id: string
  object: 'webhook_delivery'
  webhook_id: string
  event_id: string
  event_type: string
  status: string
  attempt_generation: number
  rerun_of: string | null
  last_attempt_at: string | null
  delivered_at: string | null
  created_at: string
}

// Per-attempt row inside a delivery detail. ``attempt_detail`` is the
// consumer-specific request/response blob - for webhooks that's
// {request: {url, headers, body}, response: {status, headers, body}}.
export interface PulseDeliveryAttempt {
  id: string
  object: 'webhook_delivery_attempt'
  attempt_number: number
  success: boolean
  attempt_detail: Record<string, unknown>
  error_detail: string | null
  latency_ms: number | null
  next_attempt_at: string | null
  created_at: string
}

export interface PulseDeliveryDetail extends PulseDeliveryRow {
  attempts: PulseDeliveryAttempt[]
}

export function usePulseWebhookDeliveries(
  webhookId: string | undefined,
  params?: Record<string, string | number>,
) {
  return useQuery<PaginatedResult<PulseDeliveryRow>>({
    queryKey: ['pulse-webhook-deliveries', webhookId, params],
    queryFn: () =>
      api
        .get(`/admin/webhooks/${webhookId}/deliveries/`, { params })
        .then((r) => r.data as PaginatedResult<PulseDeliveryRow>),
    staleTime: 15_000,
    enabled: Boolean(webhookId),
  })
}

export function usePulseWebhookDelivery(
  webhookId: string | undefined,
  deliveryId: string | undefined,
) {
  return useQuery<PulseDeliveryDetail>({
    queryKey: ['pulse-webhook-delivery', webhookId, deliveryId],
    queryFn: () =>
      api
        .get(`/admin/webhooks/${webhookId}/deliveries/${deliveryId}/`)
        .then((r) => r.data as PulseDeliveryDetail),
    enabled: Boolean(webhookId && deliveryId),
  })
}

// Replay a single delivery. Invalidates the list + this delivery's
// detail so the new attempt_generation row appears.
export function useReplayDelivery(webhookId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation<
    { delivery_id: string; attempt_generation: number },
    unknown,
    string
  >({
    mutationFn: (deliveryId) =>
      api
        .post(`/admin/webhooks/${webhookId}/deliveries/${deliveryId}/replay/`)
        .then((r) => r.data),
    onSuccess: (_data, deliveryId) => {
      queryClient.invalidateQueries({
        queryKey: ['pulse-webhook-deliveries', webhookId],
      })
      queryClient.invalidateQueries({
        queryKey: ['pulse-webhook-delivery', webhookId, deliveryId],
      })
    },
  })
}
